import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { PublishReadinessCard } from '@/components/producer/PublishReadinessCard';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { useProducerById } from '@/hooks/useMyProducers';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';
import { useProducerDashboardMetrics } from '@/hooks/useProducerDashboardMetrics';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import { getProducerNavVisibility } from '@/config/nav.producer';
import { DashboardCards } from '@/components/producer/DashboardCards';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Loader2,
  CalendarDays,
  Image as ImageIcon,
  CheckCircle2,
  Circle,
  Pencil,
  Camera,
  ShoppingBag,
  Clock,
  Phone,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import { useProducerEvents } from '@/hooks/useProducerEvents';
import { isFuture } from 'date-fns';
import { ShareStudioSheet } from '@/features/share/ShareStudioSheet';
import { EntityShareAction } from '@/features/share/EntityShareAction';
import { mapProducerToShareTarget } from '@/features/share/mappers/mapProducerToShareTarget';
import { producerPublicPath } from '@/lib/paths';
import { features } from '@/config/features';

// ─── Completion helpers ───

interface CompletionItem {
  label: string;
  icon: React.ElementType;
  done: boolean;
  href: string;
}

function CompletionRow({ item }: { item: CompletionItem }) {
  return (
    <Link
      to={item.href}
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/40 transition-colors group"
    >
      {item.done ? (
        <CheckCircle2 className="h-4.5 w-4.5 text-primary shrink-0" />
      ) : (
        <Circle className="h-4.5 w-4.5 text-muted-foreground/50 shrink-0" />
      )}
      <span className={`text-sm flex-1 ${item.done ? 'text-foreground' : 'text-muted-foreground'}`}>
        {item.label}
      </span>
      <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
        {item.done ? 'Redigera' : 'Lägg till'}
      </span>
    </Link>
  );
}

// ─── Quick action row ───

function QuickActionRow({ label, icon: Icon, href }: { label: string; icon: React.ElementType; href: string }) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 py-3 px-4 rounded-lg hover:bg-muted/50 transition-colors group"
    >
      <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-muted/60 shrink-0">
        <Icon className="h-4.5 w-4.5 text-muted-foreground" />
      </div>
      <span className="text-sm font-medium text-foreground flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
}

export default function ProducerDashboardPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { offerings } = useProducerOfferings(farmId !== 'demo' ? farmId : undefined);
  const [rangeDays, setRangeDays] = useState<7 | 30>(7);
  const [showShareStudio, setShowShareStudio] = useState(false);

  const { dbMetrics, trafficMetrics, isLoading } = useProducerDashboardMetrics(
    producer?.id,
    rangeDays,
  );

  const { asset: coverAsset, url1280: coverUrl1280 } = useCoverAsset('producer', producer?.id);
  const { data: events } = useProducerEvents(farmId !== 'demo' ? farmId : undefined);
  const { meta, checklist, capabilities: caps, hasCapability } = useProducerCapabilities(producer?.id);

  // Analytics
  useTrackOnce('host_dashboard_viewed', {
    producer_id: producer?.id ?? '',
    range_days: rangeDays,
  }, `dashboard-${producer?.id}`);

  const hasUpcoming = events?.some(
    (e) => e.status === 'public' && isFuture(new Date(e.start_date)),
  );
  const hasCover = Boolean(coverAsset);

  // ─── Completion items ───
  const base = `/app/producer/farm/${farmId}`;
  const completionItems: CompletionItem[] = useMemo(() => {
    if (!producer) return [];
    return [
      {
        label: 'Er berättelse',
        icon: Pencil,
        done: Boolean(producer.name && producer.short_description),
        href: `${base}/plats`,
      },
      {
        label: 'Bilder & känsla',
        icon: Camera,
        done: hasCover,
        href: `${base}/plats`,
      },
      {
        label: 'Vad erbjuder du här',
        icon: ShoppingBag,
        done: offerings.length > 0,
        href: `${base}/plats`,
      },
      {
        label: 'Besöksöppet',
        icon: Clock,
        done: Boolean(producer.opening_hours_text?.trim()),
        href: `${base}/plats`,
      },
      {
        label: 'Kontakt',
        icon: Phone,
        done: Boolean(producer.phone?.trim() || producer.email?.trim()),
        href: `${base}/plats`,
      },
    ];
  }, [producer, hasCover, base]);

  const doneCount = completionItems.filter(i => i.done).length;
  const totalCount = completionItems.length;

  // ─── Producer share target ───
  const producerPublicUrl = useMemo(
    () => (producer?.slug ? `${window.location.origin}${producerPublicPath(producer.slug)}` : null),
    [producer?.slug],
  );

  const shareTarget = useMemo(() => {
    if (!producer) return null;
    return mapProducerToShareTarget({
      producerId: producer.id,
      producerName: producer.name,
      producerSlug: producer.slug ?? producer.id,
      heroImageUrl: coverUrl1280 ?? null,
      tagline: producer.short_description ?? null,
      placeName: (producer as any).place_name ?? null,
    });
  }, [producer, coverUrl1280]);

  if (producerLoading) {
    return (
      <ProducerLayout farmId={farmId} producerName="Laddar..." title="Överblick">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout farmId={farmId} producerName={producer?.name} title="Överblick">
      <div className="space-y-6">
        {/* Header + range toggle */}
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-foreground">Överblick</h1>
          <div className="flex items-center gap-2">
            {producer?.status === 'public' && producerPublicUrl && (
              <EntityShareAction
                entityType="producer"
                publicUrl={producerPublicUrl}
                socialAction={features.enableShareStudio && shareTarget ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowShareStudio(true)}
                  >
                    <Sparkles className="h-4 w-4" />
                    Share Studio
                  </Button>
                ) : undefined}
              />
            )}
            <div className="flex gap-1 rounded-md border border-border bg-muted/30 p-0.5">
              <button
                onClick={() => setRangeDays(7)}
                className={`px-3 py-1 text-xs rounded-sm transition-colors ${
                  rangeDays === 7
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                7 dagar
              </button>
              <button
                onClick={() => setRangeDays(30)}
                className={`px-3 py-1 text-xs rounded-sm transition-colors ${
                  rangeDays === 30
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                30 dagar
              </button>
            </div>
          </div>
        </div>

        {/* Publish readiness – canonical from producer_capabilities_v */}
        {meta && checklist && farmId && (
          <PublishReadinessCard
            farmId={farmId}
            meta={meta}
            checklist={checklist}
            canPublish={hasCapability('publish.producer')}
            canUnpublish={hasCapability('unpublish.producer')}
          />
        )}

        {/* Metrics cards */}
        <DashboardCards
          dbMetrics={dbMetrics}
          trafficMetrics={trafficMetrics}
          isLoading={isLoading}
        />

        {/* ═══ Progressive completion ═══ */}
        {totalCount > 0 && doneCount < totalCount && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">Er närvaro</CardTitle>
                <span className="text-xs text-muted-foreground">{doneCount}/{totalCount} klart</span>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${(doneCount / totalCount) * 100}%` }}
                />
              </div>
            </CardHeader>
            <CardContent className="pt-1 pb-3">
              <div className="divide-y divide-border/50">
                {completionItems.map((item) => (
                  <CompletionRow key={item.label} item={item} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ Snabbåtgärder ═══ */}
        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-1">
            Snabbåtgärder
          </h3>
          <Card>
            <CardContent className="p-1">
              <QuickActionRow
                label="Redigera kontaktuppgifter"
                icon={Phone}
                href={`${base}/plats`}
              />
            </CardContent>
          </Card>
        </section>

        {/* Calm next steps — offering-aware */}
        {(() => {
          const vis = getProducerNavVisibility(offerings);
          const showActivityTip = !hasUpcoming && (vis.showActivities || offerings.length === 0);
          if (!showActivityTip && hasCover) return null;
          return (
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Tips
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {showActivityTip && (
                  <div className="rounded-lg border border-dashed border-border p-4">
                    <div className="flex items-start gap-3">
                      <CalendarDays className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-foreground">
                          {vis.showActivities ? 'Inga kommande aktiviteter' : 'Komplettera med en aktivitet'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {vis.showActivities
                            ? 'Skapa en aktivitet så att besökare kan boka.'
                            : 'Aktiviteter kan locka fler besökare till er gård.'}
                        </p>
                        <Button variant="outline" size="sm" className="mt-2" asChild>
                          <Link to={`/app/producer/farm/${farmId}/events/new`}>
                            {vis.showActivities ? 'Skapa aktivitet' : 'Lägg till aktivitet'}
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {!hasCover && (
                  <div className="rounded-lg border border-dashed border-border p-4">
                    <div className="flex items-start gap-3">
                      <ImageIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-foreground">Saknar omslagsbild</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          En bra bild gör att fler stannar.
                        </p>
                        <Button variant="outline" size="sm" className="mt-2" asChild>
                          <Link to={`/app/producer/farm/${farmId}/bilder`}>
                            Lägg till bild
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        })()}

      </div>

      {/* Producer Share Studio */}
      {shareTarget && (
        <ShareStudioSheet
          open={showShareStudio}
          onOpenChange={setShowShareStudio}
          target={shareTarget}
        />
      )}
    </ProducerLayout>
  );
}
